import React from 'react';
import { useUsage } from 'src/hooks/useUsage';
import 'src/styles/components/usage-indicator.css';

interface UsageIndicatorProps {
  productName: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  canUse: boolean;
  period: string;
  loading?: boolean;
}

const getUsagePercentage = (used: number, limit: number | null): number => {
  if (limit === null) {
    return 0;
  }

  // A zero (or negative) limit never reaches the bar: the component returns the
  // "not on your plan" panel before rendering one.
  if (limit <= 0) {
    return 0;
  }

  return Math.min(100, (used / limit) * 100);
};

export const UsageIndicator: React.FC<UsageIndicatorProps> = ({
  productName,
  used,
  limit,
  remaining,
  canUse,
  period,
  loading = false
}) => {
  const { isPro } = useUsage();
  const isUnlimited = limit === null;
  const usagePercentage = getUsagePercentage(used, limit);

  if (loading) {
    return (
      <section className="usage-indicator" aria-label={`${productName} usage`}>
        <div className="usage-heading">
          <span className="usage-title">Loading usage information...</span>
        </div>
      </section>
    );
  }

  // A zero limit means the product is excluded from this plan (the free tier
  // mostly), not that a quota ran out — a 0 / 0 meter reading "limit reached"
  // would be misleading, so name the real situation and point at the plans.
  if (limit === 0) {
    return (
      <section className="usage-indicator" aria-label={`${productName} usage`}>
        <div className="usage-heading">
          <span className="usage-title">Monthly {productName.toLowerCase()} usage</span>
          <span className="usage-period">Period · {period}</span>
        </div>
        <div className="usage-limit-message">
          <p>Not included in your plan.</p>
          <p>
            <a href="/pricing">Upgrade your plan</a> to create {productName.toLowerCase()}.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="usage-indicator" aria-label={`${productName} usage`}>
      <div className="usage-heading">
        <span className="usage-title">Monthly {productName.toLowerCase()} usage</span>
        <span className="usage-period">Period · {period}</span>
      </div>

      <div className="usage-metric">
        <div className="usage-metric-header">
          <span>{productName}</span>
          <span>
            {isUnlimited
              ? used
              : `${used} / ${limit}`}
          </span>
        </div>
        {isUnlimited ? (
          <span className="usage-meta usage-meta--unlimited">
            Unlimited {productName.toLowerCase()}
            {isPro() && <span className="usage-tier-badge"> (Pro)</span>}
          </span>
        ) : (
          <>
            <div
              className="usage-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuenow={used}
              aria-valuemax={limit ?? undefined}
            >
              <div
                className={`usage-bar-fill${canUse ? '' : ' is-disabled'}`}
                style={{ width: `${usagePercentage}%` }}
              />
            </div>
            <span className={`usage-meta${canUse ? '' : ' usage-meta-warning'}`}>
              {canUse
                ? `${remaining ?? 0} remaining this month`
                : 'Monthly limit reached'}
            </span>
          </>
        )}
      </div>

      {!canUse && !isUnlimited && (
        <div className="usage-limit-message">
          <p>You've reached your monthly limit for {productName.toLowerCase()}.</p>
          {/* Pro is not unlimited for every product (slideshows, lesson plans and
              units of work keep monthly caps), so promise a higher limit and let
              the pricing page state the exact numbers. Pro users have nothing to
              upgrade to, so they only see the reset message. */}
          {isPro() ? (
            <p>Your limit will reset next month.</p>
          ) : (
            <p>
              Your limit will reset next month, or you can{' '}
              <a href="/pricing">upgrade to Pro</a> for a higher monthly limit.
            </p>
          )}
        </div>
      )}
    </section>
  );
};
